import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';

const MarketplaceMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, [timeRange]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const data = await adminService?.getMarketplaceMetrics();
      setMetrics(data);
    } catch (error) {
      console.error('Error loading marketplace metrics:', error);
      setMetrics({
        activeListings: 0, totalBookings: 0, monthlyRevenue: 0,
        activeUsers: 0, totalUsers: 0, averageBookingValue: 0,
        conversionRate: 0, categoryBreakdown: []
      });
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    {
      label: 'Active Listings',
      value: metrics?.activeListings?.toLocaleString() || '0',
      icon: 'Package',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: 'Total Bookings',
      value: metrics?.totalBookings?.toLocaleString() || '0',
      icon: 'Calendar',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      label: 'Total Revenue',
      value: `$${((metrics?.monthlyRevenue || 0) / 1000)?.toFixed(1)}K`,
      icon: 'DollarSign',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      label: 'Total Users',
      value: metrics?.totalUsers?.toLocaleString() || '0',
      icon: 'Users',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      label: 'Avg Booking Value',
      value: `$${metrics?.averageBookingValue || 0}`,
      icon: 'TrendingUp',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: 'Booking Rate',
      value: `${metrics?.conversionRate || 0}%`,
      icon: 'Target',
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mr-3" />
        <span className="font-body text-muted-foreground">Loading marketplace metrics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-xl font-semibold text-foreground">Marketplace Overview</h2>
          <div className="flex items-center gap-2">
            {['7d', '30d', '90d', '1y']?.map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-md font-body text-sm font-medium transition-smooth ${
                  timeRange === range ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {range === '7d' && 'Last 7 Days'}
                {range === '30d' && 'Last 30 Days'}
                {range === '90d' && 'Last 90 Days'}
                {range === '1y' && 'Last Year'}
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={loadMetrics}>
              <Icon name="RefreshCw" size={14} strokeWidth={2} />
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpiCards?.map((kpi, index) => (
          <div key={index} className="bg-card rounded-lg shadow-elevation-2 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 ${kpi?.bgColor} rounded-full flex items-center justify-center`}>
                <Icon name={kpi?.icon} size={20} strokeWidth={2} className={kpi?.color} />
              </div>
            </div>
            <p className="font-heading text-2xl font-bold text-foreground mb-1">{kpi?.value}</p>
            <p className="font-caption text-sm text-muted-foreground">{kpi?.label}</p>
          </div>
        ))}
      </div>

      {/* Category Breakdown */}
      {metrics?.categoryBreakdown?.length > 0 && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="PieChart" size={20} strokeWidth={2} className="text-primary" />
            <span>Listing Categories</span>
          </h3>
          <div className="space-y-4">
            {metrics?.categoryBreakdown?.map((category, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-body text-sm text-foreground capitalize">{category?.category?.replace(/_/g, ' ')}</span>
                  <span className="font-body text-sm font-medium text-muted-foreground">
                    {category?.count} ({category?.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-500"
                    style={{ width: `${category?.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {metrics?.categoryBreakdown?.length === 0 && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-8 text-center">
          <Icon name="Package" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-body text-muted-foreground">No listing data available yet</p>
        </div>
      )}
    </div>
  );
};

export default MarketplaceMetrics;